import { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, FileType, ChevronDown } from 'lucide-react';
import { Button } from '@centraoneplus/ui';
import { exportData, ExportColumn, ExportFormat, ExportTable, generateExportFilename } from '@utils/export';

interface ExportButtonProps<T> {
  data?: T[];
  onFetchData?: () => Promise<any[]>;
  columns: ExportColumn<T>[];
  filename?: string;
  sheetName?: string;
  /** Optional table-name title shown above the primary table in the export. */
  primaryTitle?: string;
  /** Optional one-line explanation shown under the primary table's title. */
  primaryDescription?: string;
  /**
   * Optional additional tables to include in the same export (extra worksheets
   * in XLSX, extra sections in CSV/PDF). Resolved lazily on export so the data
   * is only fetched when the user actually exports.
   */
  getExtraSheets?: () => Promise<ExportTable<any>[]> | ExportTable<any>[];
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  /** Extra classes for the trigger button, so it can match a page's toolbar. */
  className?: string;
}

export function ExportButton<T>({
  data = [],
  onFetchData,
  columns,
  filename = 'export',
  sheetName = 'Data',
  primaryTitle,
  primaryDescription,
  getExtraSheets,
  disabled = false,
  variant = 'outline',
  size = 'md',
  className = '',
}: ExportButtonProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

   const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setIsOpen(false);
    // For PDF, open the print window NOW — synchronously within the click
    // gesture — so the popup isn't blocked after the async data fetch below.
    // (CSV/XLSX download via a blob and don't need this.)
    const pdfWindow = format === 'pdf' ? window.open('', '_blank') : null;
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const rows = onFetchData ? await onFetchData() : data;
      const extraSheets = getExtraSheets ? await getExtraSheets() : undefined;
      exportData({
        data: rows,
        columns,
        filename: generateExportFilename(filename),
        format,
        sheetName,
        primaryTitle,
        primaryDescription,
        extraSheets,
        pdfWindow,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={variant}
        size={size}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting || (!onFetchData && !data?.length)}
        className={`flex items-center gap-2 ${className}`}
      >
        <Download className="h-4 w-4" />
        <span>{isExporting ? 'Exporting...' : 'Export'}</span>
        <ChevronDown className="h-3 w-3" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="py-1">
            <button
              onClick={() => handleExport('csv')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FileText className="h-4 w-4 text-green-600" />
              <div className="text-left">
                <div className="font-medium">CSV</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Comma-separated values</div>
              </div>
            </button>

            <button
              onClick={() => handleExport('xlsx')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
              <div className="text-left">
                <div className="font-medium">Excel</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Microsoft Excel format</div>
              </div>
            </button>

            <button
              onClick={() => handleExport('pdf')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FileType className="h-4 w-4 text-red-600" />
              <div className="text-left">
                <div className="font-medium">PDF</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Print to PDF</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface SimpleExportButtonProps<T> {
  data?: T[];
  onFetchData?: () => Promise<any[]>;
  columns: ExportColumn<T>[];
  filename?: string;
  format: ExportFormat;
  sheetName?: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

export function SimpleExportButton<T>({
  data,
  onFetchData,
  columns,
  filename = 'export',
  format,
  sheetName = 'Data',
  disabled = false,
  variant = 'outline',
  size = 'md',
  label,
}: SimpleExportButtonProps<T>) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const rows = onFetchData ? await onFetchData() : (data ?? []);

      exportData({
        data: rows,
        columns,
        filename: generateExportFilename(filename),
        format,
        sheetName,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const Icon = format === 'csv' ? FileText : format === 'pdf' ? FileType : FileSpreadsheet;
  const defaultLabel = format === 'csv' ? 'Export CSV' : format === 'pdf' ? 'Export PDF' : 'Export Excel';

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleExport}
      disabled={disabled || isExporting || (!onFetchData && !data?.length)}
      className="flex items-center gap-2"
    >
      <Icon className="h-4 w-4" />
      <span>{isExporting ? 'Exporting...' : (label || defaultLabel)}</span>
    </Button>
  );
}
